import { ReactNode } from 'react'
import { CheckCircleIcon, MemberIcon, QualifyIcon, UnlockIcon } from './icons'
import { Reveal } from './reveal'
import { Sphere } from './sphere'

export interface CardStep {
  title: string
  points: string[]
}

const GLYPHS: ReactNode[] = [
  <UnlockIcon key="unlock" />,
  <MemberIcon key="member" />,
  <QualifyIcon key="qualify" />,
]

/** How the card is unlocked, what it gives, and how to qualify. */
export function GetCardTab({ steps }: { steps: CardStep[] }) {
  return (
    <div className="mx-auto flex max-w-[1280px] flex-col items-center gap-10 lg:flex-row lg:justify-center lg:gap-8">
      <LockedCard />

      <div className="flex w-full flex-col gap-8 lg:w-[577px] lg:shrink-0">
        {steps.map((step, index) => (
          <Reveal key={step.title} index={index} className="flex items-start gap-4 sm:gap-6">
            <span className="flex size-[52px] shrink-0 items-center justify-center rounded-[12px] bg-card text-muted-foreground sm:size-[62px] [&>svg]:size-6 sm:[&>svg]:size-7">
              {GLYPHS[index % GLYPHS.length]}
            </span>

            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <span className="text-base leading-6 text-muted-foreground/70">
                {String(index + 1).padStart(2, '0')}
              </span>

              <div className="flex flex-col gap-4">
                <h3 className="text-2xl font-semibold leading-8 text-foreground">{step.title}</h3>

                <ul className="flex flex-col gap-3">
                  {step.points.map((point) => (
                    <li key={point} className="flex items-center gap-4 sm:gap-[22px]">
                      <CheckCircleIcon className="size-5 shrink-0 text-muted-foreground" />
                      <span className="flex-1 text-base leading-6 text-muted-foreground">
                        {point}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  )
}

/**
 * The card on its spheres, laid out as a share of one box so the whole
 * composition scales together instead of drifting apart on a narrow screen.
 */
function LockedCard() {
  return (
    <Reveal className="flex w-full justify-center lg:w-[436px] lg:shrink-0">
      {/* Stacked, the larger sphere rises above the box, so room is kept for it. */}
      <div className="w-full max-w-[436px]">
        <div className="relative aspect-[436/384] w-full max-lg:mt-[15.3%]">
          <Sphere name="sphere-lg" className="left-[47.9%] top-[-17.3%] w-[47.3%]" />
          <Sphere name="sphere-sm" className="left-[61.9%] top-[64.2%] w-[24.8%]" />
          <img
            src="/images/vip-card/locked-card.png"
            alt=""
            width={432}
            height={291}
            loading="lazy"
            className="absolute left-0 top-[5.8%] w-full rotate-[0.83deg] transition-transform duration-500 ease-smooth hover:rotate-0 hover:scale-[1.02]"
          />
        </div>
      </div>
    </Reveal>
  )
}
